/**
 * Microphone Button Component
 * Main button to start/stop voice calls
 */

import { motion } from 'framer-motion';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import type { MicrophoneButtonProps } from '@/types';
import { cn } from '@/utils/cn';

export function MicrophoneButton({
  isActive,
  isProcessing,
  onClick,
}: MicrophoneButtonProps) {
  const baseClasses =
    'relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 shadow-2xl';

  const stateClasses = {
    active: 'bg-red-500 hover:bg-red-600',
    processing: 'bg-gray-400',
    idle: 'bg-nomade-accent hover:bg-nomade-accent/90',
  };

  const getStateClass = () => {
    if (isProcessing) return stateClasses.processing;
    if (isActive) return stateClasses.active;
    return stateClasses.idle;
  };

  const Icon = isProcessing ? Loader2 : isActive ? MicOff : Mic;

  return (
    <div className="relative">
      {/* Pulse animation when active */}
      {isActive && (
        <>
          <motion.div
            className="absolute inset-0 rounded-full bg-red-500"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.5, 0, 0.5],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            className="absolute inset-0 rounded-full bg-red-500"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.3, 0, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.3,
            }}
          />
        </>
      )}

      {/* Main button */}
      <motion.button
        onClick={onClick}
        disabled={isProcessing}
        className={cn(baseClasses, getStateClass())}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Icon
          className={cn(
            'h-10 w-10 text-white',
            isProcessing && 'animate-spin'
          )}
        />
      </motion.button>
    </div>
  );
}
